import { createSlice } from "@reduxjs/toolkit";
// api
import { checkCanPrintAPI, buyLabelOrderAPI, buyLabelShipmentAPI, getRateAPI, initExportShipmentAPI, voidLabelAPI, downloadLabelAPI, getInsuranceRateAPI, updateOrderPackageAPI, createDraftAPI, cancelDraftAPI, editDraftAPI, queryTaskResultAPI } from "src/api";
// redux
import { batch } from "src/redux/store";
import { recursiveCheckTaskById, updateOrderRefreshStatus } from "src/redux/slices/task";
import { getList as getOrderList } from "src/redux/slices/order";
// sections
import { fRatesList } from "src/sections/shipment/utility/format-data";
// utils
import apiSlice from "src/utils/api-slice";
// ----------------------------------------------------------------------

const defaultShipmentFilter = {
    shipment_status: "all",
    tracking_status: "all",
    provider_id: "all",
    // keyword: "",
    shipment_id: "",
    tracking_number: "",
    page: 1,
    page_size: 25,
    total: 0,
    start_date: "",
    end_date: "",
    quick_date_range: "all",
};


const defaultShipmentDraftFilter = {
    from_name: "",
    to_name: "",
    // quick_date_range: "last_7_days",
    page: 1,
    page_size: 25,
    total: 0,
    start_date: "",
    end_date: "",
    // ...getDefaultDraftDateRange()
};

const initialState = {
    isLoading: false,
    error: null,
    defaultDimensionUnit: 'UK',

    // ----------------------------------
    shipment: {
        id: null,
        company_id: null,
        order_id: null,
        rate_request: null,
        rate_result: null
    },

    shipmentFilter: {
        ...defaultShipmentFilter
    },

    shipmentDraftFilter: {
        ...defaultShipmentDraftFilter
    },

    draft: null,
    quickRate: {
        rate_request: null
    },
    canPrint: true
};

const slice = createSlice({
    name: "shipment",
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },

        stopLoading(state) {
            state.isLoading = false;
        },

        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            //
            state.error = action.payload;
        },

        clearError(state) {
            state.error = null;
        },

        updateShipmentListFilter(state, action) {
            state.shipmentFilter = {
                ...state.shipmentFilter,
                ...action.payload
            };
        },

        resetShipmentListFilter(state) {
            state.shipmentFilter = {
                ...state.shipmentFilter,
                //
                shipment_status: "all",
                tracking_status: "all",
                provider_id: "all",
                shipment_id: "",
                tracking_number: "",
                // keyword: "",
                page: 1,
                page_size: 25,
                start_date: "",
                end_date: "",
                quick_date_range: "all",
            };
        },

        updateShipmentDraftListFilter(state, action) {
            state.shipmentDraftFilter = {
                ...state.shipmentDraftFilter,
                ...action.payload
            };
        },
        resetShipmentDraftListFilter(state) {
            state.shipmentDraftFilter = {
                ...state.shipmentDraftFilter,
                //
                from_name: "",
                to_name: "",
                // quick_date_range: "last_7_days",
                page: 1,
                page_size: 25,
                start_date: "",
                end_date: "",
                // ...getDefaultDraftDateRange()
            };
        },


        // UPDATE QUICK RATE
        updateQuickRate(state, action) {
            state.quickRate = {
                ...state.quickRate,
                ...action.payload
            };
        },

        // RESET QUICK RATE
        resetQuickRate(state) {
            state.quickRate = {
                rate_request: null
            };
        },

        updateCanPrint(state, action) {
            state.canPrint = action.payload || false;
        }
    }
});

// Reducer
export default slice.reducer;

// Actions
export const {
    startLoading,
    stopLoading,
    //
    updateShipmentListFilter,
    resetShipmentListFilter,
    updateShipmentDraftListFilter,
    resetShipmentDraftListFilter,
    //
    updateQuickRate,
    resetQuickRate,
    //
    clearError
} = slice.actions;

// ----------------------------------------------------------------------

export function createShipmentDraft(request) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());


            const { status, data } = await createDraftAPI({ ...request });
            if (status === 200 && data?.code === "ok") {
                batch(() => {
                    dispatch(apiSlice.util.invalidateTags([ "ShipmentDraftList" ]));
                });
                return data.data
            }
            return false;

        } catch (e) {
            // dispatch(slice.actions.hasError(e));
            //
            return e;
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function editShipmentDraft(request) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());


            const { status } = await editDraftAPI({ ...request });

            if (status === 200) {
                batch(() => {
                    dispatch(apiSlice.util.invalidateTags([ "ShipmentDraftList" ]));
                    // dispatch(slice.actions.resetQuickRate());
                });
                return true

            }
            return false;

        } catch (e) {
            // dispatch(slice.actions.hasError(e));
            //
            return e;
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}
// ----------------------------------------------------------------------

export function getRatesOrder(request, id) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const packageRequest = {
                order_id: id,
                package_data: {
                    ...request.package_data
                }
            };

            const responses = await Promise.all([
                updateOrderPackageAPI({ ...packageRequest }),
                getRateAPI({ ...request })
            ]);

            const { status: statusA, data: dataA } = responses[ 0 ];
            const { status: statusB, data: dataB } = responses[ 1 ];

            if (statusA === 200 && dataA?.code === "ok") {
                dispatch(getOrderList());
            }

            return ( statusB === 200 && dataB?.code === "ok" ) ? dataB?.data : null;
        } catch (e) {
            // dispatch(slice.actions.hasError(e));
            //
            return null;
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

// ----------------------------------------------------------------------

export function getRatesQuickRate(request) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());
            const { status, data } = await getRateAPI({ ...request });

            return status === 200 ? (data?.data || []) : false;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return e;
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

// ----------------------------------------------------------------------

export function buyLabelShipment(request) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status, data } = await buyLabelShipmentAPI({ ...request });

            if (status === 200) {
                batch(() => {
                    dispatch(apiSlice.util.invalidateTags([ "ShipmentList", "ShipmentDraftList" ]));
                });
                //
                return data?.code === "ok" ? true : null;
            }

            return null;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return null;
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

// ----------------------------------------------------------------------

export function voidLabel(request) {
    return async (dispatch) => {
        try {
            const { status } = await voidLabelAPI({ ...request });

            if (status === 200) {
                dispatch(apiSlice.util.invalidateTags([ "ShipmentList", "ShipmentDraftList" ]));
            }

            return status === 200;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return false;
        }
    };
}


export function deleteDraft(shipment_id) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status } = await cancelDraftAPI(shipment_id);

            if (status === 200) {
                dispatch(apiSlice.util.invalidateTags([ "ShipmentDraftList" ]));
            }

            return status === 200;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return false;
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

// ----------------------------------------------------------------------

export function downloadShipmentLabel(id) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const request = {
                shipment_id: id,
            };

            const { status, data } = await downloadLabelAPI({ ...request });

            if (status === 200) {
                return data
            }
            return null
        } catch (e) {
            dispatch(slice.actions.hasError(e));
                        return null

        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

// ----------------------------------------------------------------------

export function checkCanPrint() {
    return async (dispatch) => {
        try {
            const { status, data } = await checkCanPrintAPI();

            if (status === 200) {
                dispatch(slice.actions.updateCanPrint(data?.data));
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}


export function checkTaskById(ids) {
    return async (dispatch) => {
        try {
            const response = await queryTaskResultAPI({
                params: {
                    task_ids: ids.join(','),
                },
            });

            const { status, data } = response;

            if (status === 200 && data?.code === 'ok') {
                return data?.data ?? {};
            }

            return {};
        } catch (e) {
            dispatch(slice.actions.hasError(e));

            return {};
        }
    };
}